import React from 'react';
import { PostHog, PostHogProvider as DefaultPostHogProvider, usePostHog } from 'posthog-js/react';
import { PostHogConfig } from 'posthog-js';
import { POSTHOG_API_HOST, POSTHOG_API_KEY } from 'src/env.json';

const isEnabled = () => !!POSTHOG_API_KEY;

// Some WebView / sandboxed iframe contexts throw `ReferenceError: Can't find
// variable: localStorage` on access. Detect once and fall back to cookie-only
// persistence so PostHog never touches a missing global.
const hasUsableLocalStorage = (): boolean => {
  try {
    const probe = '__ph_ls_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

export const customPosthogEvent = {
  klarnaFail: 'klarna_fail',
  mobileAppDownloadClicked: 'mobile_app_download_clicked',
  klarnaPending: 'klarna_pending',
  klarnaSuccess: 'klarna_success',
  onboardSubscribeSuccess: 'onboard_subscribe_success',
  subscribeSuccess: 'subscribe_success',
  subscriptionCancellationDialogClosed: 'subscription_cancellation_dialog_closed',
  subscriptionCancellationDialogOpened: 'subscription_cancellation_dialog_opened',
  subscriptionCancellationFeedbackSubmitted: 'subscription_cancellation_feedback_submitted',
  subscriptionEditorChangeRequested: 'subscription_editor_change_requested',
  subscriptionOneTimeOfferApplied: 'subscription_one_time_offer_applied',
  requestRevisionRated: 'request_revision_rated',
  recurringPaymentSuccess: 'recurring_payment_success',
  trustpilotReviewGuideTriggered: 'trustpilot_review_guide_triggered',
} as const;

export const postHogOptions: Partial<PostHogConfig> = {
  api_host: POSTHOG_API_HOST,
  persistence: hasUsableLocalStorage() ? 'localStorage+cookie' : 'cookie',
};

export const PostHogProvider: React.FC<React.ComponentProps<typeof DefaultPostHogProvider>> = ({
  children,
  ...props
}) => (
  <DefaultPostHogProvider apiKey={POSTHOG_API_KEY} options={postHogOptions} {...props}>
    {children}
  </DefaultPostHogProvider>
);

export const useDecoratedPostHog = (): {
  raw: PostHog;
  capture: PostHog['capture'];
  identify: PostHog['identify'];
  reset: PostHog['reset'];
  group: PostHog['group'];
  isFeatureEnabled: PostHog['isFeatureEnabled'];
} => {
  const posthog = usePostHog();

  // PostHog internals occasionally reach for storage APIs that are unavailable
  // in restricted contexts (mobile WebView, sandboxed iframes). Wrap each call
  // so a storage/permissions failure never bubbles up as an unhandled error.
  const safeCall = <T,>(label: string, fn: () => T, fallback?: T): T | undefined => {
    try {
      return fn();
    } catch (err) {
      console.warn(`posthog.${label} failed`, err);
      return fallback;
    }
  };

  return {
    raw: posthog,
    capture: (event, properties, options) => {
      const newProps = properties ? { custom: properties } : properties;

      if (!isEnabled()) {
        console.debug('posthog.capture', event, newProps, options);

        return;
      }

      return safeCall('capture', () => posthog?.capture(event, newProps, options));
    },
    identify: (...args) => {
      if (!isEnabled()) {
        console.debug('posthog.identify', ...args);
        return;
      }

      safeCall('identify', () => posthog?.identify(...args));
    },
    reset: (...args) => {
      if (!isEnabled()) {
        console.debug('posthog.reset', ...args);
        return;
      }

      safeCall('reset', () => posthog?.reset(...args));
    },
    group: (...args) => {
      if (!isEnabled()) {
        console.debug('posthog.group', ...args);
        return;
      }

      safeCall('group', () => posthog?.group(...args));
    },
    isFeatureEnabled: (...args) => {
      if (!isEnabled()) {
        console.debug('posthog.isFeatureEnabled', ...args);

        return true;
      }

      return safeCall('isFeatureEnabled', () => posthog?.isFeatureEnabled(...args), true);
    },
  };
};
